import { useState } from 'react';
import { supabase } from '../supabase';
import {
  buildOrderUpdates,
  type BuildOrderCurrentItem
} from './buildOrderUpdates';

/**
 * The single, direct, UNGATED build-order write path (005-build-project Phase 5).
 *
 * Given ONE SIBLING GROUP's current work items and a TARGET sequence of that
 * group's ids, it persists `work_items.build_order` so the group's order matches
 * the requested sequence — and NOTHING else. 05-project-hub-build-order scoped
 * `build_order` per sibling group (items sharing one `parent_work_item_id`):
 * callers pass exactly one group as `currentItems` and a FULL permutation of it
 * as `sequence` (anything partial is refused — see `buildOrderUpdates`). Both
 * reorder sources call this exact path: the person's manual drag (phase 6) and
 * the agent's live reorder (phase 7).
 *
 * What it does — and deliberately does NOT — do:
 *   - Writes ONLY `work_items.build_order`. It NEVER writes
 *     `work_item_dependencies` (the resolved Job-C rule: a reorder is a soft
 *     preference; dependencies are the hard fact and are untouched). A blocked
 *     item dropped above its blocker is written at its new order with its
 *     blocked-by edge intact — the contradiction is shown (phase 4's note),
 *     never prevented or auto-resolved.
 *   - Is ORG- and PROJECT-scoped: every update carries `.eq('id', <row>)` AND
 *     `.eq('org_id', activeOrgId)`, and only this project's item ids are ever
 *     touched (a sequence referencing a foreign id is refused, not written
 *     cross-project). Mirrors `useSaveProject`'s scoped-`.update().eq` shape.
 *   - Is DIRECT — it does NOT route through `create_document` / `ApprovalCard` /
 *     the directive-approval write-gate. Maintaining the project's own plan is a
 *     direct edit, not a sign-off write. No approval card ever appears.
 *   - Is IDEMPOTENT + MINIMAL: re-applying the current order is a no-op; only the
 *     rows whose `build_order` actually changed are written.
 *   - Writes nothing outside the active org / open project, and NEVER to any
 *     repo or codebase (there is no codebase write surface in this path).
 *
 * After a successful write, the surface's existing `work_items` realtime
 * subscription fires `reload()`, so the Build Order section reflects the new
 * order live (proven end-to-end in phases 6–8).
 */

export interface UseReorderBuildOrder {
  /** True while a reorder write is in flight. */
  pending: boolean;
  /** The last write error (or a refused foreign-id sequence), else null. */
  error: string | null;
  /**
   * Persist `sequence` (a FULL permutation of one sibling group's work-item ids)
   * to `work_items.build_order`, scoped to `orgId` + the group passed as
   * `currentItems`. Writes only changed rows; re-applying the current order is a
   * no-op. Returns true on success (including a no-op), false on a write failure
   * or a refused sequence — a foreign id, a duplicate, or an incomplete
   * permutation (error is set; nothing is written).
   */
  reorder: (
    orgId: string,
    currentItems: ReadonlyArray<BuildOrderCurrentItem>,
    sequence: ReadonlyArray<string>
  ) => Promise<boolean>;
}

export function useReorderBuildOrder(): UseReorderBuildOrder {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reorder = async (
    orgId: string,
    currentItems: ReadonlyArray<BuildOrderCurrentItem>,
    sequence: ReadonlyArray<string>
  ): Promise<boolean> => {
    setPending(true);
    setError(null);
    try {
      const plan = buildOrderUpdates(currentItems, sequence);

      // A sequence referencing an id outside this project is refused — never
      // written cross-project. Surface the error; raise no approval card.
      if (!plan.valid) {
        setError(plan.reason ?? 'Invalid reorder sequence');
        return false;
      }

      // Idempotent no-op: nothing changed, so no write is issued.
      if (plan.updates.length === 0) {
        return true;
      }

      // Direct, ungated, org- + project-scoped write — ONLY work_items.build_order,
      // one scoped update per changed row. Never work_item_dependencies; never
      // through create_document / ApprovalCard.
      for (const update of plan.updates) {
        const { error: upErr } = await supabase
          .from('work_items')
          .update({ build_order: update.build_order })
          .eq('id', update.id)
          .eq('org_id', orgId);
        if (upErr) {
          setError(upErr.message);
          return false;
        }
      }

      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error reordering build order');
      return false;
    } finally {
      setPending(false);
    }
  };

  return { pending, error, reorder };
}
